<template>
    <div class="d-flex flex-row rounded bg-light-light p-10 gap-10">
        <div class="flex-grow-1 d-flex flex-column gap-3">
            <div class="d-flex flex-row gap-1">
                <Badge v-if="isCancelledOrder" type="badge-danger" class="text-white">
                    <template v-slot:icon>
                        <i class="fas fa-exclamation-triangle"/>
                    </template>
                    Order was cancelled. No further actions possible.
                </Badge>
                <Badge v-else-if="isRejectedOrder" type="badge-danger" class="text-white">
                    <template v-slot:icon>
                        <i class="fas fa-exclamation-triangle"/>
                    </template>
                    Order was rejected. No further actions possible.
                </Badge>
                <Badge v-else-if="shippingDetails.package_send" icon="fas fa-check" type="badge-success">
                    Shipped
                </Badge>
                <Badge v-else-if="shippingDetails.package_prepared && !shippingDetails.package_send"
                       type="badge-warning" class="text-white">
                    <template v-slot:icon>
                        <Spinner class="d-inline-block mr-1 mb-n1"
                                 :size="12"
                                 :speed="1.8"
                                 line-bg-color="white"
                                 line-fg-color="transparent"/>
                    </template>
                    Waiting for pickup
                </Badge>
                <Badge v-else-if="!shippingDetails.package_prepared && !isCancelledOrRejectedOrder" type="badge-danger" class="text-white">
                    <template v-slot:icon>
                        <i class="fas fa-exclamation-triangle"/>
                    </template>
                    Prepare package or reject
                </Badge>
                <Badge v-if="isAmazonFlexDelivery" type="badge-info" icon="fas fa-truck">
                    Pickup by Amazon delivery driver
                </Badge>
                <Badge v-else icon="fa fa-user" type="badge-info">
                    Pickup by Customer
                </Badge>
            </div>
            <div>
                <div class="text-uppercase text-muted font-size-xs">
                    Package Number
                </div>
                <div class="font-weight-light font-size-h2">
                    {{ shippingDetails.package_number || 'Not set' }}
                </div>
            </div>
            <div>
                <div class="d-flex flex-row gap-3">
                    <div v-if="hasShippingLabel">
                        <img class="w-50px h-90px"
                             :src="`data:image/${shippingDetails.meta?.document?.format?.toLowerCase()};base64,${shippingDetails.meta?.document?.content}`"
                             alt="Shipping Label Image">
                    </div>
                    <table>
                        <tr v-if="shippingDetails.dimensions">
                            <td class="font-weight-bolder">Dimensions</td>
                            <td class="pl-3">
                                <ValueWithUnit :value="shippingDetails.dimensions.width" :decimals="2"/>
                                x
                                <ValueWithUnit :value="shippingDetails.dimensions.length" :decimals="2"/>
                                x
                                <ValueWithUnit :value="shippingDetails.dimensions.height" :decimals="2"/>
                                (w/l/h)
                            </td>
                        </tr>
                        <tr v-if="shippingDetails.weight">
                            <td class="font-weight-bolder">Weight</td>
                            <td class="pl-3">
                                <ValueWithUnit :value="shippingDetails.weight"/>
                            </td>
                        </tr>
                        <tr v-if="shippingDetails.hazmat_labels?.length > 0">
                            <td class="font-weight-bolder">Hazmat</td>
                            <td class="pl-3">
                                <ul>
                                    <li v-for="label in shippingDetails.hazmat_labels">
                                        {{ label }}
                                    </li>
                                </ul>
                            </td>
                        </tr>
                        <tr>
                            <td class="font-weight-bolder">Transport</td>
                            <td class="pl-3">
                                {{ shippingDetails.transport_service || '' }}
                            </td>
                        </tr>
                        <tr>
                            <td class="font-weight-bolder">Tracking Code</td>
                            <td class="pl-3">
                                {{ shippingDetails.tracking_code || 'Not available' }}
                            </td>
                        </tr>
                    </table>
                </div>
                <OrderItems class="table-sm" :order="order" :show-total="false" show-description/>
            </div>
        </div>
        <div v-if="!isCancelledOrRejectedOrder" class="d-flex flex-column justify-content-center gap-2">
            <!-- ACCEPT & REJECT -->
            <button v-if="!shippingDetails.package_prepared" class="btn btn-light-success px-4" @click="markPrepared">
                <i class="fas fa-check-square"/>
                Prepared & ready for pickup
            </button>
            <button v-if="!shippingDetails.package_prepared && !shippingDetails.package_send"
                    class="btn btn-light-danger px-4" disabled>
                <i class="fas fa-times-circle"/>
                Reject this package
            </button>

            <!-- PRINT SHIPPING LABEL -->
<!--            <a v-if="!shippingDetails.package_send" class="btn btn-secondary px-4"-->
<!--               :href="`data:image/${shippingDetails.meta?.document?.format?.toLowerCase()};base64,${shippingDetails.meta?.document?.content}`"-->
<!--               target="_new">-->
<!--                <i class="fas fa-print"/>-->
<!--                Download shipping label-->
<!--            </a>-->
            <button v-if="!shippingDetails.package_send" class="btn btn-secondary px-4" @click="printLabel">
                <i class="fas fa-print"/>
                Print shipping label
            </button>

            <!-- COLLECTED -->
            <button v-if="shippingDetails.package_prepared && !shippingDetails.package_send"
                    class="btn btn-light-success px-4" @click="markCollected">
                <i class="fas fa-check-square"/>
                Package was collected
            </button>
        </div>
    </div>
</template>

<script>
import Badge from "../../Badge.vue";
import ValueWithUnit from "../../ValueWithUnit.vue";
import Spinner from "vue-simple-spinner";
import OrderItems from "../Tabs/OrderItems.vue";
import {OrderState} from '@composables/orderStates';

export default {
    components: {OrderItems, ValueWithUnit, Badge, Spinner},

    props: {
        store: Object,
        order: Object,
        shippingDetails: Object
    },

    methods: {
        markPrepared() {
            this.$inertia.post(this.$route('order.shipments.prepared', [this.store.id, this.order.url_key, this.shippingDetails.id]), {}, {
                preserveState: false
            });
        },

        markCollected() {
            this.$inertia.post(this.$route('order.shipments.collected', [this.store.id, this.order.url_key, this.shippingDetails.id]), {}, {
                preserveState: false
            });
        },

        printLabel() {
            let w = window.open("", "Image");
            w.document.write(`
           <style>
            html, body {
    height:100%;
    margin: 0 !important;
    padding: 0 !important;
    overflow: hidden;
  }
           </style>
  `);
            w.document.write(`<img style="height: 100%;" src="data:image/${this.shippingDetails.meta?.document?.format?.toLowerCase()};base64,${this.shippingDetails.meta?.document?.content}" alt="label" />`);
            w.window.print();
            w.document.close();
        }
    },

    computed: {
        hasShippingLabel() {
            return this.shippingDetails.meta?.document?.content !== undefined
        },

        isAmazonFlexDelivery() {
            return this.shippingDetails.transport_service.includes('RUSH');
        },

        isCancelledOrder() {
            return this.order.state === OrderState.Cancelled
        },

        isRejectedOrder() {
            return this.order.state === OrderState.Rejected
        },

        isCancelledOrRejectedOrder() {
            return this.order.state === OrderState.Rejected || this.order.state === OrderState.Cancelled
        },
    }
}
</script>
